import { Menu } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  NOTIFICATION_ROUTES,
  markAllReadResponseSchema,
  notificationArchivedResponseSchema,
  notificationDeletedResponseSchema,
  myNotificationsResponseSchema,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { Button } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";
import { BrandLogo } from "@/shared/ui/site/SiteHeader/BrandLogo";
import { SessionGreeting } from "@/shared/ui/site/SiteHeader/SessionGreeting";
import { GlassPanel } from "../GlassPanel/GlassPanel";
import type { ShellUser } from "../AppShell/useShellUser";
import { PanelViewSwitcher } from "./PanelViewSwitcher";
import { NotificationsMenu } from "./NotificationsMenu";
import { TopbarAuthLinks } from "./TopbarAuthLinks";
import { UserMenu } from "./UserMenu";

export interface AppTopbarProps {
  user: ShellUser | null;
  onLogout: () => void;
  /** Abre el cajón de navegación. */
  onOpenNav: () => void;
  navOpen: boolean;
  /** id del cajón, para `aria-controls`. */
  navPanelId: string;
  className?: string;
}

/**
 * La campana, conectada: la lista y el contador salen de
 * `/api/notifications/me` (cada minuto, y al volver a la pestaña), y
 * ABRIR el panel marca todo como leído en el servidor. Vive en un hook
 * para que `AppTopbar` solo componga.
 */
function useNotifications(enabled: boolean) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["notifications", "mine"],
    queryFn: async () => {
      const data = await authFetch(NOTIFICATION_ROUTES.mine, myNotificationsResponseSchema, {
        baseUrl: "",
      });
      return data;
    },
    enabled,
    refetchInterval: 60_000,
  });

  const markAllRead = useMutation({
    mutationFn: () =>
      authFetch(NOTIFICATION_ROUTES.markAllRead, markAllReadResponseSchema, {
        baseUrl: "",
        method: "POST",
        body: {},
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications", "mine"] });
    },
  });

  /*
   * ── Archivar y eliminar (Anahí, 29-ago) ────────────────────────────
   * Las dos refrescan la lista al terminar. Se hacen por separado
   * porque significan cosas distintas: archivar conserva el aviso
   * fuera de la campana, eliminar lo borra. Decidir por la persona cuál
   * de las dos quiere no es asunto nuestro.
   */
  const archivar = useMutation({
    mutationFn: (id: string) =>
      authFetch(NOTIFICATION_ROUTES.archive(id), notificationArchivedResponseSchema, {
        baseUrl: "",
        method: "PATCH",
        body: {},
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["notifications", "mine"] }),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) =>
      authFetch(NOTIFICATION_ROUTES.remove(id), notificationDeletedResponseSchema, {
        baseUrl: "",
        method: "DELETE",
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["notifications", "mine"] }),
  });

  return {
    items: query.data?.items ?? [],
    unread: query.data?.unread ?? 0,
    onOpen: () => {
      if ((query.data?.unread ?? 0) > 0) markAllRead.mutate();
    },
    onArchive: (id: string) => archivar.mutate(id),
    onRemove: (id: string) => eliminar.mutate(id),
  };
}

/**
 * La barra superior de los paneles: EL MISMO navbar de la tienda
 * (Anahí, 27-ago, sobre capturas): "Menú" y el saludo con Salir a la
 * izquierda, el logotipo CENTRADO, y los controles a la derecha. La
 * diferencia con la tienda es doble:
 *
 * 1. Aquí la píldora NO respira: "se debe quedar así como está en esta
 *    imagen" — nace y se queda en su forma flotante y redonda, sin la
 *    animación de scroll (la mayoría de las pantallas del panel ni
 *    siquiera scrollean, así que la animación no se veía nunca).
 * 2. La derecha lleva lo que el panel necesita —idioma, campana y el
 *    menú de la cuenta— en el lugar donde la tienda pone moneda y
 *    carrito, que aquí no significan nada.
 *
 * Fuera el título "Administración · Compras": el logotipo ocupa el
 * centro como en la tienda, y el "dónde estoy" ya lo cuenta la barra
 * lateral con su elemento activo (y cada página con su encabezado).
 *
 * ── Las notificaciones ───────────────────────────────────────────────
 * La campana va SOLO con sesión iniciada (el selector de idioma que
 * estaba a su lado se retiró el 1-sep: el idioma lo decide el país):
 * enseñarle novedades a quien no ha entrado no significa nada. La lista
 * y el contador salen de `useNotifications` (arriba), que habla con
 * `/api/notifications/me`.
 */
export function AppTopbar({
  user,
  onLogout,
  onOpenNav,
  navOpen,
  navPanelId,
  className,
}: AppTopbarProps) {
  const { t } = useTranslation();
  const notifications = useNotifications(Boolean(user));

  return (
    <header className={cn("sticky top-0 z-40 px-3 pb-2 pt-3 xl:px-4 xl:pt-4", className)}>
      {/*
        ── Qué cede espacio primero, en un teléfono (Anahí, 29-ago) ─────
        Con "Menú", el selector de vistas, idioma, campana y cuenta, los
        controles no cabían en 390 px y el logotipo —posicionado en el
        centro— acababa DEBAJO de ellos, ilegible y encimado.

        El orden en que se retiran no es arbitrario: primero lo que no es
        una acción (el logotipo, que además es un enlace a la tienda que
        ya está en el menú), luego lo que tiene otra puerta (el selector
        de vistas, que vive también en la barra lateral). Lo que nunca se
        va: el menú —sin él no hay navegación—, la campana y la cuenta.
      */}
      <GlassPanel className="flex items-center gap-1.5 rounded-full bg-white/80 px-2.5 py-2 sm:gap-3 sm:px-4">
        {/* IZQUIERDA · saludo (y el menú SOLO donde no hay barra lateral) */}
        <div className="relative flex min-w-0 flex-1 basis-0 items-center gap-2">
          {/*
            "Menú" NO va en el panel de escritorio (Anahí, 27-ago): la
            barra lateral YA es la navegación y el botón era redundante.
            Bajo `lg` la lateral no existe, así que ahí el botón vuelve —
            sin él, un teléfono se queda sin forma de moverse.
          */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onOpenNav}
            aria-label={t("Abrir la navegación")}
            aria-expanded={navOpen}
            aria-controls={navPanelId}
            leftIcon={<Menu />}
            className="bg-white uppercase tracking-ds-label lg:hidden"
          >
            {t("Menú")}
          </Button>
          {user && <SessionGreeting name={user.name} className="hidden md:flex" />}
        </div>

        {/* CENTRO · logotipo. Se retira en pantallas estrechas: es un
            enlace a la tienda, no una acción del panel, y encimado no
            sirve a nadie. */}
        <BrandLogo to="/shop" className="relative hidden sm:flex" />

        {/* DERECHA · vistas (super-admin), campana y cuenta */}
        <div className="relative flex min-w-0 flex-1 basis-0 items-center justify-end gap-2">
          {/* El cambio de vista vive también en la barra lateral: bajo
              `md` cede su sitio a lo que no tiene otra puerta. */}
          {user?.role === "super_admin" && <PanelViewSwitcher className="hidden md:block" />}
          {/*
            ── Aquí vivía el selector de idioma (Anahí, 1-sep) ──────────
            «Es la única forma de cambiar el idioma y el país, para evitar
            errores de pagos.» El idioma lo decide ahora el PAÍS del
            perfil, junto con la moneda y la cuenta de Stripe.

            Dos mandos independientes permitían el estado que nadie
            quiere: alguien viendo el sitio en inglés, con precios en
            pesos, cobrando por la cuenta de México. Se cambia en
            /socio/perfil, que es donde se elige el país.
          */}
          {user ? (
            <>
              <NotificationsMenu
                count={notifications.unread}
                items={notifications.items}
                onOpen={notifications.onOpen}
                onArchive={notifications.onArchive}
                onRemove={notifications.onRemove}
              />
              <UserMenu user={user} onLogout={onLogout} />
            </>
          ) : (
            <TopbarAuthLinks />
          )}
        </div>
      </GlassPanel>
    </header>
  );
}
